import {
  evaluateTemplateValue,
  dataLocationToPath,
} from "@ea-lab/reactive-json";

/**
 * Déroule un traitement serveur PAR LOTS et publie son avancement dans le store.
 *
 * Pourquoi une réaction (échappatoire au config-first) : reactive-json ne sait
 * pas RÉPÉTER un appel. Ses actions partent une fois, dans l'ordre du YAML, et
 * `on: response` ne réamorce pas la chaîne qui l'a déclenchée — enchaîner deux
 * écritures exige déjà un artifice, en enchaîner N est hors de portée. Or un
 * traitement de masse (rattacher dix mille contacts à un segment) ne tient pas
 * dans une requête : il se découpe en tranches, et QUELQU'UN doit rappeler le
 * serveur tant qu'il reste du travail. C'est tout ce que fait ce fichier — la
 * boucle, et rien d'autre : le bouton, la barre de progression, le message de
 * fin et la modale restent du YAML.
 *
 * Générique par construction : la réaction ne connaît ni segment, ni contact,
 * ni compteur métier. Elle POSTe `body` + `{cursor, limit}` sur `url` et attend
 * du serveur, en retour, un CURSEUR et un drapeau de fin — c'est le seul contrat.
 *
 * ÉTAPE `init` (optionnelle) : un premier POST qui CRÉE le traitement côté
 * serveur (ex. un job orbit_queue) et rend son `jobId` et son `total`. Le
 * `jobId` est ensuite ajouté au corps de chaque tour. Sans elle, le premier tour
 * part directement sur `url` avec `cursor: 0`.
 *
 * Contrat de réponse (une tranche) :
 *   cursor  : strictement croissant d'un tour à l'autre (obligatoire) ;
 *   done    : true quand il n'y a plus rien à traiter (obligatoire) ;
 *   total   : le périmètre complet — retenu de `init`, ou du premier tour ;
 *   TOUT AUTRE NOMBRE : cumulé d'une tranche à l'autre et republié sous la
 *             même clé (`written`, `denied`… apparaissent dans `statePath` du
 *             seul fait que le serveur les envoie) ;
 *   scanned : cumulé comme les autres, et recopié dans `processed` (le nom
 *             que lit la barre de progression) ;
 *   clés de `latest` (ex. `remaining`, `errors`) : recopiées TELLES QUELLES à
 *             chaque tour, jamais cumulées ;
 *   clés de `mirror` ({clé: chemin}) : recopiées à chaque tour, erreurs
 *             comprises, à un AUTRE endroit du store (ex. le rapport qui
 *             alimente un tableau à côté de la barre) ;
 *   status  : {type, message} — un `type: error` interrompt la boucle.
 *
 * État publié à `statePath` (tout est là, l'écran n'a rien à calculer) :
 *   running, done, stopped, error, message, jobId, cursor, total, processed,
 *   les compteurs, percent (0-100) et width ("42%", posable tel quel en
 *   `style.width` — le YAML ne concatène pas, donc la chaîne se fabrique ici),
 *   announce : la phrase à ANNONCER aux lecteurs d'écran, écrite au DÉBUT et
 *   effacée à la fin — une région live rafraîchie à chaque tranche annoncerait
 *   cent fois la même chose. La fin est dite par les `role="alert"` de la page.
 *   Les compteurs déclarés dans l'état initial du YAML repartent de zéro à
 *   chaque lancement, pour que l'écran n'affiche jamais un « undefined ».
 *
 * ARRÊT. `cancelPath` désigne un booléen, relu à chaque tour — un chemin à
 * part, pas une clé de `statePath` : la réaction republie l'état entier à
 * chaque tranche, elle écraserait un drapeau posé pendant une requête en vol.
 * La réaction le remet à `false` au LANCEMENT (avant tout appel), sans quoi un
 * « Arrêter » précédent interromprait aussitôt le lot suivant. Avec `cancel:
 * {url}`, un arrêt est aussi signalé au serveur (`{jobId}`), qui jette le reste.
 *
 * GARDE-FOUS. Un serveur qui rendrait toujours le même curseur bouclerait sans
 * fin : la boucle s'arrête si le curseur n'avance pas, et au bout de
 * `maxIterations` tours. Un échec HTTP arrête tout et laisse le message à
 * l'écran — le travail déjà fait, lui, reste fait.
 *
 * Usage YAML (job orbit_queue) :
 *   - what: batchRun
 *     on: click
 *     init:
 *       url: "/api/orbit/queues/job"
 *       body:
 *         source: { type: elasticsearch, index: contacts, config: contacts_ids,
 *                   state: { filter: ~~.selectedFilters, search: ~~.search } }
 *         operation: { type: decoupled_patch, entityType: node, bundle: contact,
 *                      patch: [{ op: addToList, key: segments, value: ~~.bulkSegmentId }] }
 *         chunkSize: 100
 *     url: "/api/orbit/queues/job/run"
 *     cancel: { url: "/api/orbit/queues/job/cancel" }
 *     statePath: ~~.segmentBulk
 *     cancelPath: ~~.segmentBulkCancel
 *     latest: [remaining, errors, jobStatus]
 *
 * Enregistrée dans [App.jsx](../../../App.jsx) sous `customPlugins.reaction.batchRun`.
 */

// Un seul lot à la fois par `statePath` : re-cliquer pendant un traitement ne
// doit pas lancer une seconde boucle sur le même compteur (les deux
// s'écraseraient et l'avancement partirait en arrière).
const runningByKey = new Set();

const MAX_ITERATIONS = 2000;

// Clés que le cumul ne doit JAMAIS additionner : elles disent une position
// (`cursor`), un périmètre (`total`) ou un état, pas une quantité de travail fait.
const RESERVED = new Set(["cursor", "total", "done", "status", "running", "percent", "width", "announce"]);

export const batchRunReaction = async (props) => {
  const { args, globalDataContext, templateContext } = props;
  if (!args) return;

  const gdc = globalDataContext.getRootContext
    ? globalDataContext.getRootContext()
    : globalDataContext;

  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext: gdc, templateContext });

  // Résout les chemins (`~~.x`) à TOUTE profondeur du corps DÉCLARÉ, sans
  // jamais ré-évaluer une donnée déjà résolue : un corps de job porte ses
  // chemins trois ou quatre niveaux plus bas (`source.state.filter`,
  // `operation.patch[0].value`), hors de portée d'une évaluation à profondeur
  // fixe — et une valeur de filtre commençant par `~.` ne doit pas être relue
  // comme un chemin.
  const resolveTree = (node) => {
    if (Array.isArray(node)) return node.map(resolveTree);
    if (node && typeof node === "object") {
      return Object.fromEntries(Object.entries(node).map(([k, v]) => [k, resolveTree(v)]));
    }
    return typeof node === "string" ? resolve(node) : node;
  };

  const toPath = (loc) =>
    typeof loc === "string"
      ? dataLocationToPath({
          dataLocation: loc,
          currentPath: templateContext?.templatePath,
          globalDataContext: gdc,
          templateContext,
        })
      : null;

  const url = resolve(args.url);
  if (!url) return;

  const statePath = toPath(args.statePath);
  if (!statePath) {
    console.error("batchRun: `statePath` manquant — sans lui, rien ne peut s'afficher.");
    return;
  }

  const key = JSON.stringify(statePath);
  if (runningByKey.has(key)) return;
  runningByKey.add(key);

  // Un « Arrêter » du lot précédent ne doit pas interrompre celui-ci. Écrit
  // AVANT tout appel : aucune requête n'est encore en vol pour l'écraser.
  const cancelPath = toPath(args.cancelPath);
  if (cancelPath) gdc.updateData(false, cancelPath);
  const isCancelled = () =>
    args.cancelPath ? resolve(args.cancelPath) === true : false;

  const baseBody = args.body ? resolveTree(args.body) : {};
  const limit = Number(resolve(args.limit)) || 100;
  const maxIterations = Number(resolve(args.maxIterations)) || MAX_ITERATIONS;
  const latest = new Set(Array.isArray(args.latest) ? args.latest : []);
  // `mirror: { cléDeRéponse: chemin }` — recopie une clé de chaque réponse
  // AILLEURS dans le store (ex. le rapport qui alimente un tableau). Sans cela,
  // ce que l'écran affiche à côté de la barre resterait figé pendant la boucle.
  const mirror = args.mirror && typeof args.mirror === "object"
    ? Object.entries(args.mirror).map(([k, loc]) => [k, toPath(loc)]).filter(([, p]) => p)
    : [];

  const state = {
    running: true,
    done: false,
    stopped: false,
    error: "",
    message: "",
    // Annoncé UNE fois, au démarrage (cf. l'en-tête).
    announce: resolve(args.startMessage) || "Traitement en cours…",
    jobId: "",
    cursor: 0,
    total: 0,
    processed: 0,
    scanned: 0,
    percent: 0,
    // Toujours une chaîne non vide : reactive-json JETTE les attributs falsy,
    // un `width: ""` laisserait la barre à sa largeur par défaut.
    width: "0%",
  };
  // Les compteurs et les clés `latest` que le YAML déclare dans son état
  // initial repartent de leur valeur vide : l'écran les lit dès le premier
  // rendu, avant qu'un tour ne les ait publiés.
  const seed = resolve(args.statePath);
  if (seed && typeof seed === "object") {
    for (const [k, v] of Object.entries(seed)) {
      if (k in state || RESERVED.has(k)) continue;
      if (typeof v === "number") state[k] = 0;
      else if (latest.has(k)) state[k] = Array.isArray(v) ? [] : typeof v === "string" ? "" : v;
    }
  }
  const publish = () => gdc.updateData({ ...state }, statePath);

  const applyProgress = () => {
    state.percent = state.total > 0
      ? Math.min(100, Math.round((state.processed / state.total) * 100))
      : (state.done ? 100 : 0);
    state.width = `${state.percent}%`;
  };

  const post = async (target, body) => {
    const response = await fetch(target, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(gdc.headersForRjBuild || {}),
      },
      body: JSON.stringify(body),
    });
    let payload = {};
    try {
      payload = await response.json();
    } catch {
      /* corps non JSON — traité comme une erreur par l'appelant */
    }
    return { response, payload };
  };

  const failed = ({ response, payload }) => !response.ok || payload?.status?.type === "error";
  const errorOf = ({ response, payload }) =>
    payload?.status?.message ||
    resolve(args.errorMessage) ||
    `Le traitement a échoué (HTTP ${response.status}).`;

  publish();

  try {
    let extraBody = {};

    if (args.init) {
      const initUrl = resolve(args.init.url);
      if (!initUrl) {
        state.error = "batchRun : `init.url` manquant — le traitement ne peut pas être créé.";
        return;
      }
      const created = await post(initUrl, args.init.body ? resolveTree(args.init.body) : {});
      if (failed(created)) {
        state.error = errorOf(created);
        return;
      }
      state.jobId = created.payload.jobId ?? "";
      state.total = Number(created.payload.total) || 0;
      extraBody = { jobId: state.jobId };
      applyProgress();
      publish();
    }

    for (let i = 0; i < maxIterations; i++) {
      if (isCancelled()) {
        state.stopped = true;
        break;
      }
      const tranche = await post(url, { ...baseBody, ...extraBody, cursor: state.cursor, limit });
      // Recopié AVANT le contrôle d'échec : une réponse d'erreur peut porter,
      // elle aussi, l'état à jour (ex. le rapport des files sur un 422).
      for (const [k, p] of mirror) {
        if (tranche.payload && k in tranche.payload) gdc.updateData(tranche.payload[k], p);
      }
      if (failed(tranche)) {
        state.error = errorOf(tranche);
        break;
      }
      const { payload } = tranche;

      // Le périmètre : celui de `init`, sinon celui du PREMIER tour (un curseur
      // étant en général une clause de la requête, les suivants ne comptent
      // que le reste).
      if (!args.init && i === 0 && Number.isFinite(Number(payload.total))) {
        state.total = Number(payload.total);
      }
      for (const [k, value] of Object.entries(payload)) {
        if (RESERVED.has(k)) continue;
        if (latest.has(k)) {
          state[k] = value;
          continue;
        }
        // Cumul GÉNÉRIQUE, NOMBRES seulement : `Number([])` vaut 0 et
        // `Number(true)` vaut 1 — un tableau ou un booléen ne se cumulent pas.
        if (typeof value !== "number" || !Number.isFinite(value)) continue;
        state[k] = (Number(state[k]) || 0) + value;
      }
      state.processed += Number(payload.scanned) || 0;
      state.message = payload?.status?.message || "";

      const nextCursor = Number(payload.cursor);
      const stalled = !Number.isFinite(nextCursor) || nextCursor <= state.cursor;
      state.cursor = Number.isFinite(nextCursor) ? nextCursor : state.cursor;

      if (payload.done === true) {
        state.done = true;
        break;
      }
      // Le serveur dit « pas fini » mais n'avance plus : on s'arrête plutôt
      // que de marteler la même tranche indéfiniment.
      if (stalled) {
        state.error =
          resolve(args.errorMessage) ||
          "Le traitement s'est interrompu : le serveur ne progresse plus.";
        break;
      }

      applyProgress();
      publish();
    }

    // Arrêt demandé, ou échec en cours de route : le serveur jette ce qui reste.
    // Sinon la file attendrait son expiration — et « Relancer » crée de toute
    // façon un nouveau job.
    if ((state.stopped || state.error) && !state.done && args.cancel?.url && state.jobId) {
      await post(resolve(args.cancel.url), { jobId: state.jobId }).catch(() => {});
    }
  } catch (err) {
    console.error("batchRun:", err);
    state.error =
      resolve(args.errorMessage) ||
      "Le traitement a échoué. Le travail déjà effectué est conservé ; relancez pour terminer.";
  } finally {
    state.running = false;
    // La région live se tait : la fin est dite par les blocs `role="alert"`
    // (succès, arrêt, erreur), qui la porteraient en double sinon.
    state.announce = "";
    applyProgress();
    if (state.done) {
      state.percent = 100;
      state.width = "100%";
    }
    publish();
    runningByKey.delete(key);
  }
};
